import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, TrendingUp, Link2, AlertTriangle, BarChart3,
  ChevronDown, ExternalLink, ArrowUpRight, ArrowDownRight,
  Target, Layers, FileText, Zap, Filter, Activity
} from "lucide-react";

/* ── mock data ── */
const articles = [
  { id: 1, title: "How AI Is Reshaping Cloud Security in 2026", url: "/ai-cloud-security-2026", score: 92, keyword: "AI cloud security", volume: 8400, position: 3, trend: "up", issues: 0 },
  { id: 2, title: "Top 10 SaaS Tools for Remote Teams", url: "/saas-tools-remote-teams", score: 78, keyword: "SaaS remote tools", volume: 5200, position: 8, trend: "up", issues: 2 },
  { id: 3, title: "Zero Trust Architecture Explained", url: "/zero-trust-architecture", score: 85, keyword: "zero trust architecture", volume: 12100, position: 5, trend: "down", issues: 1 },
  { id: 4, title: "Machine Learning in FinTech: A 2026 Guide", url: "/ml-fintech-guide", score: 64, keyword: "machine learning fintech", volume: 3800, position: 14, trend: "up", issues: 4 },
  { id: 5, title: "The Rise of Edge Computing", url: "/edge-computing-rise", score: 71, keyword: "edge computing", volume: 9900, position: 11, trend: "down", issues: 3 },
  { id: 6, title: "Cybersecurity Trends to Watch", url: "/cybersecurity-trends", score: 88, keyword: "cybersecurity trends", volume: 14500, position: 4, trend: "up", issues: 1 },
];

const keywordClusters = [
  { name: "Cloud Security", keywords: 12, avgPosition: 6.2, totalVolume: 42000, coverage: 85, color: "bg-primary" },
  { name: "AI & ML", keywords: 18, avgPosition: 9.4, totalVolume: 38500, coverage: 62, color: "bg-warning" },
  { name: "DevOps", keywords: 8, avgPosition: 4.1, totalVolume: 28000, coverage: 91, color: "bg-primary" },
  { name: "FinTech", keywords: 14, avgPosition: 12.8, totalVolume: 22000, coverage: 45, color: "bg-destructive" },
  { name: "Remote Work", keywords: 6, avgPosition: 7.5, totalVolume: 19000, coverage: 72, color: "bg-warning" },
];

const linkSuggestions = [
  { from: "AI Cloud Security", to: "Zero Trust Architecture", anchor: "zero trust model", relevance: 94 },
  { from: "SaaS Tools for Remote Teams", to: "The Rise of Edge Computing", anchor: "edge infrastructure", relevance: 87 },
  { from: "Cybersecurity Trends", to: "AI Cloud Security", anchor: "AI-driven security", relevance: 91 },
  { from: "ML in FinTech", to: "SaaS Tools for Remote Teams", anchor: "SaaS integrations", relevance: 76 },
  { from: "Zero Trust Architecture", to: "Cybersecurity Trends", anchor: "latest trends", relevance: 82 },
];

const contentGaps = [
  { keyword: "quantum computing security", volume: 6800, difficulty: 42, opportunity: 95, competitors: 3 },
  { keyword: "AI compliance regulations", volume: 4200, difficulty: 38, opportunity: 88, competitors: 2 },
  { keyword: "serverless security best practices", volume: 7500, difficulty: 55, opportunity: 79, competitors: 5 },
  { keyword: "devsecops pipeline", volume: 5100, difficulty: 47, opportunity: 84, competitors: 4 },
  { keyword: "web3 enterprise adoption", volume: 3600, difficulty: 31, opportunity: 91, competitors: 1 },
];

type Tab = "articles" | "clusters" | "linking" | "gaps";

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? "text-success bg-success/15" : score >= 60 ? "text-warning bg-warning/15" : "text-destructive bg-destructive/15";
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums ${color}`}>{score}</span>;
}

function StatCard({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function SEOCenterPage() {
  const [tab, setTab] = useState<Tab>("articles");

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: "articles", label: "Article Scores", icon: FileText },
    { id: "clusters", label: "Keyword Clusters", icon: Layers },
    { id: "linking", label: "Internal Linking", icon: Link2 },
    { id: "gaps", label: "Content Gaps", icon: Target },
  ];

  const avgScore = Math.round(articles.reduce((a, b) => a + b.score, 0) / articles.length);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6">
        <div>
          <h1 className="text-lg font-semibold text-foreground">SEO Center</h1>
          <p className="text-xs text-muted-foreground">Optimization, audits & keyword tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/seo/monitor"
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            <Activity className="h-4 w-4" />
            Site Monitor
          </Link>
          <button className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
            <Zap className="h-4 w-4" />
            Run Full Audit
          </button>
        </div>
      </header>

      <div className="p-6 space-y-6 animate-slide-in">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={BarChart3} label="Avg SEO Score" value={`${avgScore}/100`} sub="+3 from last week" />
          <StatCard icon={TrendingUp} label="Ranking Keywords" value="58" sub="12 in top 5" />
          <StatCard icon={Link2} label="Internal Links" value="342" sub="18 orphan pages" />
          <StatCard icon={AlertTriangle} label="Issues Found" value="11" sub="4 critical" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === "articles" && <ArticleScores />}
        {tab === "clusters" && <KeywordClusters />}
        {tab === "linking" && <InternalLinking />}
        {tab === "gaps" && <ContentGaps />}
      </div>
    </div>
  );
}

/* ── Article Scores Tab ── */
function ArticleScores() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 flex-1 max-w-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1" placeholder="Search articles..." />
        </div>
        <button className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Filter className="h-4 w-4" />
          Filter
        </button>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Article</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">SEO Score</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Primary Keyword</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Volume</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Position</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Issues</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {articles.map((a) => (
              <tr key={a.id} className="hover:bg-accent/30 transition-colors cursor-pointer">
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.url}</p>
                </td>
                <td className="px-4 py-3"><ScoreBadge score={a.score} /></td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{a.keyword}</td>
                <td className="px-4 py-3 text-sm tabular-nums text-foreground">{a.volume.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1 text-sm tabular-nums text-foreground">
                    #{a.position}
                    {a.trend === "up" ? (
                      <ArrowUpRight className="h-3.5 w-3.5 text-success" />
                    ) : (
                      <ArrowDownRight className="h-3.5 w-3.5 text-destructive" />
                    )}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {a.issues === 0 ? (
                    <span className="text-xs text-success font-medium">Clean</span>
                  ) : (
                    <span className="text-xs text-warning font-medium">{a.issues} issues</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Keyword Clusters Tab ── */
function KeywordClusters() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {keywordClusters.map((c) => (
        <div key={c.name} className="rounded-lg border border-border bg-card p-5 hover:border-primary/40 transition-colors cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">{c.name}</h3>
            <span className="text-xs text-muted-foreground">{c.keywords} keywords</span>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Avg Position</span>
              <span className="text-foreground font-medium tabular-nums">#{c.avgPosition}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Total Volume</span>
              <span className="text-foreground font-medium tabular-nums">{c.totalVolume.toLocaleString()}</span>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">Coverage</span>
                <span className="text-foreground font-medium">{c.coverage}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-accent overflow-hidden">
                <div className={`h-full rounded-full ${c.color} transition-all`} style={{ width: `${c.coverage}%` }} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Internal Linking Tab ── */
function InternalLinking() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">AI-generated internal linking suggestions to strengthen topic clusters and improve crawlability.</p>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">From Article</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Link To</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Suggested Anchor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Relevance</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {linkSuggestions.map((l, i) => (
              <tr key={i} className="hover:bg-accent/30 transition-colors">
                <td className="px-4 py-3 text-sm text-foreground">{l.from}</td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1.5 text-sm text-primary">
                    {l.to}
                    <ExternalLink className="h-3 w-3" />
                  </span>
                </td>
                <td className="px-4 py-3">
                  <code className="rounded bg-accent px-2 py-0.5 text-xs font-mono text-foreground">{l.anchor}</code>
                </td>
                <td className="px-4 py-3"><ScoreBadge score={l.relevance} /></td>
                <td className="px-4 py-3">
                  <button className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors">
                    Apply
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Content Gaps Tab ── */
function ContentGaps() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Keywords your competitors rank for that you don't yet cover. Sorted by opportunity score.</p>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Keyword</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Volume</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Difficulty</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Opportunity</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Competitors</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contentGaps.map((g, i) => (
              <tr key={i} className="hover:bg-accent/30 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-foreground">{g.keyword}</td>
                <td className="px-4 py-3 text-sm tabular-nums text-foreground">{g.volume.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-16 rounded-full bg-accent overflow-hidden">
                      <div
                        className={`h-full rounded-full ${g.difficulty < 40 ? "bg-success" : g.difficulty < 60 ? "bg-warning" : "bg-destructive"}`}
                        style={{ width: `${g.difficulty}%` }}
                      />
                    </div>
                    <span className="text-xs tabular-nums text-muted-foreground">{g.difficulty}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><ScoreBadge score={g.opportunity} /></td>
                <td className="px-4 py-3 text-sm tabular-nums text-muted-foreground">{g.competitors} ranking</td>
                <td className="px-4 py-3">
                  <button className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors">
                    Create Article
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
